/**
 * JarvisHud — cinematic "Iron Man" command-center takeover for the Home page.
 * Rendered full-screen when Jarvis is active (admin). Matrix rain background,
 * rotating arc-reactor rings, live clock, weather and live platform data.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, Users, Mail, Calendar, ListTodo, CloudSun } from "lucide-react";
import { useJarvisStatus } from "./jarvisStatus";
import RichContent from "./RichContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CY = "#22d3ee";
const OR = "#f59e0b";

const STYLE = `
@keyframes jvspin { to { transform: rotate(360deg); } }
@keyframes jvspinr { to { transform: rotate(-360deg); } }
@keyframes jvpulse { 0%,100% { opacity:.55; } 50% { opacity:1; } }
@keyframes jvfade { from { opacity:0; transform: translateY(10px);} to { opacity:1; transform:none;} }
@keyframes jvscan { 0%{transform:translateY(-100%)} 100%{transform:translateY(2000%)} }
@keyframes jvzoom { from { opacity:0; transform: translate(-50%,-50%) scale(.78);} to { opacity:1; transform: translate(-50%,-50%) scale(1);} }
@keyframes jvkenburns { from { transform: scale(1);} to { transform: scale(1.14);} }
.jv-panel { animation: jvfade .6s ease both; }
`;

function dayGroupLabel(s) {
  if (!s) return "Mai demult";
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return "Mai demult";
  const d0 = new Date(); d0.setHours(0, 0, 0, 0);
  const diff = Math.round((d0 - new Date(dt).setHours(0, 0, 0, 0)) / 86400000);
  if (diff <= 0) return "Azi";
  if (diff === 1) return "Ieri";
  if (diff < 7) return dt.toLocaleDateString([], { weekday: "long" });
  return dt.toLocaleDateString([], { day: "2-digit", month: "long" });
}

function fmtMailDate(s) {
  if (!s) return "";
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return "";
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  return sameDay ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
}

function fmtCond(c) {
  if (!c) return "—";
  return c.replace(/_/g, " ").replace("day", "").replace("night", "").trim();
}

export default function JarvisHud({ authHeader, onExit }) {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [d, setD] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  // In-HUD photo lightbox (open big photo + swipe) — never exits Jarvis.
  const [hudLightbox, setHudLightbox] = useState(null); // { items:[{url,name,id}], idx }
  const touchXRef = useRef(null);
  const lbNext = () => setHudLightbox((lb) => lb && ({ ...lb, idx: (lb.idx + 1) % lb.items.length }));
  const lbPrev = () => setHudLightbox((lb) => lb && ({ ...lb, idx: (lb.idx - 1 + lb.items.length) % lb.items.length }));
  useEffect(() => {
    if (!hudLightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setHudLightbox(null);
      else if (e.key === "ArrowRight") lbNext();
      else if (e.key === "ArrowLeft") lbPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hudLightbox]);

  const go = (path) => { if (onExit) onExit(); navigate(path); };
  const clickable = { cursor: "pointer" };

  // Cinematic briefing focus (driven by the Jarvis widget while it narrates).
  const { briefing } = useJarvisStatus();
  const [spotIdx, setSpotIdx] = useState(0);
  const spotPhotos = briefing?.photos || [];
  useEffect(() => {
    setSpotIdx(0);
    if (!spotPhotos.length) return;
    const iv = setInterval(() => setSpotIdx((i) => (i + 1) % spotPhotos.length), 1700);
    return () => clearInterval(iv);
  }, [briefing?.focusId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/doserne/jarvis/hud`, authHeader());
        if (alive) setD(data);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [authHeader]);

  // Matrix rain
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf, w, h, cols, drops;
    const chars = "ジャービスJARVIS01アイオン0123456789ドサーネ".split("");
    const setup = () => {
      w = cv.width = window.innerWidth;
      h = cv.height = window.innerHeight;
      cols = Math.floor(w / 16);
      drops = Array(cols).fill(0).map(() => Math.random() * -50);
    };
    setup();
    window.addEventListener("resize", setup);
    let frame = 0;
    const draw = () => {
      frame++;
      ctx.fillStyle = "rgba(3,7,17,0.10)";
      ctx.fillRect(0, 0, w, h);
      ctx.font = "14px monospace";
      if (frame % 2 === 0) {
        for (let i = 0; i < cols; i++) {
          const txt = chars[Math.floor(Math.random() * chars.length)];
          const x = i * 16;
          const y = drops[i] * 16;
          ctx.fillStyle = Math.random() > 0.97 ? OR : "rgba(34,211,238,0.55)";
          ctx.fillText(txt, x, y);
          if (y > h && Math.random() > 0.975) drops[i] = 0;
          drops[i] += 0.5;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setup); };
  }, []);

  const w = d?.weather || {};
  const team = d?.team || {};
  const signedPct = team.members ? Math.round((team.signed / team.members) * 100) : 0;

  const Ring = ({ size, dur, rev, dash, color, op }) => (
    <div style={{
      position: "absolute", width: size, height: size, borderRadius: "50%",
      border: `2px ${dash ? "dashed" : "solid"} ${color}`, opacity: op,
      left: "50%", top: "50%", marginLeft: -size / 2, marginTop: -size / 2,
      animation: `${rev ? "jvspinr" : "jvspin"} ${dur}s linear infinite`,
      boxShadow: `0 0 24px ${color}44, inset 0 0 24px ${color}22`,
    }} />
  );

  const Node = ({ label, top, left }) => (
    <div style={{
      position: "absolute", top, left, transform: "translate(-50%,-50%)",
      width: 30, height: 30, borderRadius: "50%", background: `${OR}22`,
      border: `1.5px solid ${OR}`, color: OR, fontSize: 11, fontWeight: 800,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: `0 0 12px ${OR}66`, animation: "jvpulse 2.4s ease-in-out infinite",
    }}>{label}</div>
  );

  return (
    <div data-testid="jarvis-hud" style={{
      position: "fixed", inset: 0, zIndex: 60, background: "#030711",
      fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: CY, overflow: "hidden",
    }}>
      <style>{STYLE}</style>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, opacity: 0.28 }} />
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 45%, rgba(34,211,238,0.10), transparent 55%)" }} />

      {/* Exit */}
      <button type="button" onClick={onExit} data-testid="jarvis-hud-exit" style={{
        position: "absolute", top: 18, right: 18, zIndex: 5, display: "flex", alignItems: "center", gap: 6,
        padding: "8px 14px", borderRadius: 999, border: `1px solid ${CY}55`, background: "rgba(3,7,17,.6)",
        color: CY, fontWeight: 800, fontSize: 12, cursor: "pointer", letterSpacing: 1,
      }}>
        <X size={14} /> EXIT
      </button>

      {/* Top center: date + clock (+ weather in line with the clock) */}
      <div className="jv-panel" style={{ position: "absolute", top: 24, left: "50%", transform: "translateX(-50%)", textAlign: "center", zIndex: 4 }}>
        <div style={{ fontSize: 12, letterSpacing: 4, color: `${CY}aa` }}>{(d?.date || "").toUpperCase()}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 18 }}>
          <div data-testid="jarvis-hud-clock" style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, textShadow: `0 0 24px ${CY}88`, letterSpacing: 4 }}>
            {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          {/* Weather beside the clock */}
          <div data-testid="jarvis-hud-weather" style={{ display: "flex", alignItems: "center", gap: 8, textAlign: "left", borderLeft: `1px solid ${CY}33`, paddingLeft: 16 }}>
            <CloudSun size={22} color={OR} style={{ opacity: 0.9 }} />
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", lineHeight: 1, textShadow: `0 0 14px ${CY}55` }}>
                {w.temperature_c != null ? `${Math.round(w.temperature_c)}°` : "—"}
              </div>
              <div style={{ fontSize: 10, color: `${CY}cc`, textTransform: "capitalize", marginTop: 3 }}>
                {fmtCond(w.condition)}{w.wind_ms != null ? ` · ${w.wind_ms} m/s` : ""}
              </div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, letterSpacing: 6, color: OR, marginTop: 4 }}>J.A.R.V.I.S. — CONTROL ONLINE</div>
      </div>

      {/* Center: projects gallery (last 10, image + name) over faint reactor rings */}
      <div style={{ position: "absolute", left: "50%", top: "54%", transform: "translate(-50%,-50%)", width: 700, maxWidth: "72vw", zIndex: 4 }}>
        <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 560, height: 560, pointerEvents: "none" }}>
          <Ring size={560} dur={40} rev dash color={CY} op={0.16} />
          <Ring size={440} dur={26} color={CY} op={0.2} />
          <Ring size={330} dur={16} rev dash color={OR} op={0.18} />
        </div>
        {/* Projects orbit — tiles rotate slowly around a circle; hover to pause
            and enlarge, click to open the project */}
        {(() => {
          const projs = (d?.projects?.recent || []).slice(0, 10);
          const n = projs.length || 1;
          const R = 175;
          const paused = briefing || hoverIdx !== null;
          return (
            <div style={{ position: "relative", width: 440, height: 440, maxWidth: "72vw", margin: "0 auto",
              animation: "jvspin 90s linear infinite", animationPlayState: paused ? "paused" : "running",
              opacity: briefing ? 0.16 : 1, filter: briefing ? "blur(2px)" : "none", transition: "opacity .4s ease" }}>
              {projs.map((p, i) => {
                const Ai = (i / n) * 360;
                const hovered = hoverIdx === i;
                return (
                  <div key={p.id || i} style={{ position: "absolute", left: "50%", top: "50%", width: 0, height: 0, transform: `rotate(${Ai}deg) translate(${R}px)` }}>
                    <div style={{ transform: `rotate(${-Ai}deg)` }}>
                      <div style={{ animation: "jvspinr 90s linear infinite", animationPlayState: paused ? "paused" : "running" }}>
                        <div onClick={() => {
                            if (p.image) {
                              const items = (d?.projects?.recent || []).slice(0, 10)
                                .filter((x) => x.image).map((x) => ({ url: x.image, name: x.name, id: x.id }));
                              const idx = Math.max(0, items.findIndex((x) => x.id === p.id && x.url === p.image));
                              setHudLightbox({ items, idx });
                            } else {
                              go(p.id ? `/doserne/prosjekter/${p.id}` : "/doserne/prosjekter");
                            }
                          }}
                          onMouseEnter={() => setHoverIdx(i)} onMouseLeave={() => setHoverIdx((h) => (h === i ? null : h))}
                          data-testid="hud-project-tile" title={p.name}
                          style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
                            width: hovered ? 138 : 86, height: hovered ? 138 : 86, borderRadius: 12, overflow: "hidden", cursor: "pointer",
                            border: `${hovered ? 2 : 1}px solid ${hovered ? CY : CY + "44"}`,
                            background: p.image ? "#02060d" : `linear-gradient(135deg, ${p.color || CY}33, #02060d)`,
                            boxShadow: hovered ? `0 0 30px ${CY}` : `0 0 12px ${CY}22`, zIndex: hovered ? 30 : 1,
                            transition: "width .2s ease, height .2s ease, box-shadow .2s ease, border-color .2s ease" }}>
                          {p.image
                            ? <img src={p.image} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: hovered ? 1 : 0.82 }} />
                            : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: hovered ? 40 : 26, fontWeight: 800, color: `${p.color || CY}` }}>{(p.name || "?").trim()[0]?.toUpperCase()}</div>}
                          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "12px 6px 4px", background: "linear-gradient(transparent, rgba(2,6,13,0.96))" }}>
                            <div style={{ fontSize: hovered ? 11 : 9.5, color: "#fff", fontWeight: 700, lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.name}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* reactor core (count) — counter-rotated so it stays STILL */}
              <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none" }}>
                <div style={{ animation: "jvspinr 90s linear infinite", animationPlayState: paused ? "paused" : "running" }}>
                  <div style={{ width: 84, height: 84, borderRadius: 999, border: `1px solid ${CY}55`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: `radial-gradient(circle, ${CY}18, transparent 70%)`, boxShadow: `0 0 30px ${CY}22 inset` }}>
                    <div data-testid="jarvis-hud-projects" style={{ fontSize: 30, fontWeight: 800, color: "#fff", textShadow: `0 0 18px ${CY}`, lineHeight: 1 }}>{d?.projects?.active ?? "—"}</div>
                    <div style={{ fontSize: 8, letterSpacing: 2, color: `${CY}aa`, marginTop: 2 }}>ACTIVE</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Briefing spotlight — the project Jarvis is talking about, with
            yesterday's photos cycling + who posted them */}
        {briefing?.phase === "project" && (
          <div data-testid="hud-briefing-spotlight"
            style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 400, maxWidth: "62vw", zIndex: 8,
              background: "rgba(2,8,18,0.94)", border: `2px solid ${briefing.color || CY}`, borderRadius: 18, overflow: "hidden",
              boxShadow: `0 0 46px ${briefing.color || CY}77`, animation: "jvzoom .5s cubic-bezier(.2,.8,.2,1) both" }}>
            <div style={{ height: 230, background: "#02060d", position: "relative" }}>
              {spotPhotos.length ? (
                <img key={spotIdx} src={spotPhotos[spotIdx]?.url} alt="progress"
                  style={{ width: "100%", height: "100%", objectFit: "cover", animation: "jvkenburns 6s ease-out both" }} />
              ) : (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 52, fontWeight: 800, color: briefing.color || CY }}>{(briefing.focusName || "?").trim()[0]?.toUpperCase()}</div>
              )}
              {spotPhotos[spotIdx]?.author && (
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "18px 12px 8px", background: "linear-gradient(transparent, rgba(2,8,18,0.95))", fontSize: 12, color: "#cbe8f2" }}>
                  📷 {spotPhotos[spotIdx].author}
                </div>
              )}
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.15 }}>{briefing.focusName}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 11, color: briefing.color || CY, fontWeight: 700 }}>Ieri · {spotPhotos.length} {spotPhotos.length === 1 ? "poză" : "poze"}</span>
                {(briefing.authors || []).length > 0 && <span style={{ fontSize: 11, color: `${CY}aa` }}>· {briefing.authors.join(", ")}</span>}
              </div>
              {spotPhotos.length > 1 && (
                <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
                  {spotPhotos.map((_, i) => (
                    <span key={i} style={{ width: i === spotIdx ? 16 : 6, height: 6, borderRadius: 999, background: i === spotIdx ? (briefing.color || CY) : `${CY}44`, transition: "width .3s ease" }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email spotlight — the message Jarvis is reading, opened with a zoom */}
        {briefing?.phase === "email" && briefing.email && (
          <div data-testid="hud-email-spotlight"
            style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 430, maxWidth: "66vw", zIndex: 8,
              background: "rgba(2,8,18,0.96)", border: `2px solid ${briefing.email.unread ? OR : CY}`, borderRadius: 18, overflow: "hidden",
              boxShadow: `0 0 46px ${briefing.email.unread ? OR : CY}66`, animation: "jvzoom .45s cubic-bezier(.2,.8,.2,1) both" }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${CY}22`, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 999, background: `${CY}1e`, border: `1px solid ${CY}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Mail size={18} color={CY} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{briefing.email.from}</div>
                <div style={{ fontSize: 10.5, color: `${CY}aa` }}>{fmtMailDate(briefing.email.date)}</div>
              </div>
              {briefing.email.unread && <span style={{ fontSize: 9, fontWeight: 800, color: "#031018", background: OR, borderRadius: 999, padding: "2px 8px", letterSpacing: 1 }}>NECITIT</span>}
            </div>
            <div style={{ padding: "14px 16px" }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: `${CY}88`, fontWeight: 800, marginBottom: 5 }}>SUBIECT</div>
              <div style={{ fontSize: 15, color: "#e6f6fc", fontWeight: 600, lineHeight: 1.35 }}>{briefing.email.subject || "(fără subiect)"}</div>
              {briefing.email.preview && (
                <>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, color: `${CY}88`, fontWeight: 800, margin: "12px 0 5px" }}>PE SCURT</div>
                  <div style={{ fontSize: 12.5, color: "#a9c9d6", lineHeight: 1.5, maxHeight: 120, overflow: "hidden" }}>{briefing.email.preview}</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Top-left: inbox — unread count + latest messages */}
      <div className="jv-panel" data-testid="hud-email-panel"
        style={{ ...panel("left"), top: 96, width: 360 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PanelTitle icon={<Mail size={13} />} text="EMAIL" />
          <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: d?.emails?.unread ? OR : `${CY}cc`, background: `${d?.emails?.unread ? OR : CY}18`, border: `1px solid ${d?.emails?.unread ? OR : CY}44`, borderRadius: 999, padding: "2px 9px" }}>
            {d?.emails?.unread ?? "—"} necitite
          </span>
        </div>
        <div data-testid="hud-email-list" style={{ maxHeight: "44vh", overflowY: "auto", marginTop: 8, display: "flex", flexDirection: "column", gap: 1 }}>
          {(d?.emails?.recent || []).length === 0 && (
            <div style={{ fontSize: 11, color: `${CY}88`, padding: "8px 2px" }}>Nicio conexiune la inbox…</div>
          )}
          {(() => {
            const items = (d?.emails?.recent || []).slice(0, 15);
            let lastLabel = null;
            return items.map((m, i) => {
              const label = dayGroupLabel(m.date);
              const showHeader = label !== lastLabel;
              lastLabel = label;
              const eFocused = briefing?.phase === "email" && briefing.email?.uid === m.uid;
              return (
                <div key={m.uid || i}>
                  {showHeader && (
                    <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: `${CY}99`, fontWeight: 800, padding: "8px 8px 3px", position: "sticky", top: 0, background: "rgba(3,10,22,0.92)" }}>{label}</div>
                  )}
                  <button type="button" onClick={() => go("/doserne/email")} data-testid="hud-email-item"
                    ref={(el) => { if (el && eFocused) el.scrollIntoView({ block: "nearest", behavior: "smooth" }); }}
                    style={{ textAlign: "left", background: eFocused ? `${CY}33` : (m.unread ? `${CY}12` : "transparent"), border: eFocused ? `1px solid ${CY}` : "1px solid transparent", borderRadius: 8, padding: "6px 8px", cursor: "pointer", display: "block", width: "100%", transform: eFocused ? "scale(1.04)" : "scale(1)", boxShadow: eFocused ? `0 0 20px ${CY}55` : "none", transition: "transform .25s ease, box-shadow .25s ease, background .25s ease" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {m.unread ? <span style={{ width: 6, height: 6, borderRadius: 999, background: OR, flexShrink: 0 }} /> : <span style={{ width: 6, flexShrink: 0 }} />}
                      <span style={{ flex: 1, fontSize: 11.5, fontWeight: m.unread ? 800 : 600, color: m.unread ? "#fff" : "#9fc7d6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.from || "—"}</span>
                      <span style={{ fontSize: 9.5, color: `${CY}77`, flexShrink: 0 }}>{fmtMailDate(m.date)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: m.unread ? "#cbe8f2" : "#6f9aa8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: 12 }}>{m.subject || "(fără subiect)"}</div>
                  </button>
                </div>
              );
            });
          })()}
        </div>
        <div onClick={() => go("/doserne/email")} style={{ ...clickable, marginTop: 8, fontSize: 10, color: `${CY}99`, display: "flex", alignItems: "center", gap: 5 }}>▸ Deschide căsuța</div>
      </div>

      {/* Bottom-left: team (small) */}
      <div className="jv-panel" onClick={() => go("/doserne/kolleger")} data-testid="hud-go-team" title="Deschide colegii"
        style={{ ...panel("left"), bottom: 40, top: "auto", width: 200, ...clickable }}>
        <PanelTitle icon={<Users size={13} />} text="ECHIPĂ" />
        <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{team.members ?? "—"}</div>
        <div style={{ height: 7, borderRadius: 4, background: "#0f2733", overflow: "hidden", marginTop: 6 }}>
          <div style={{ width: `${signedPct}%`, height: "100%", background: CY, boxShadow: `0 0 10px ${CY}` }} />
        </div>
        <div style={{ fontSize: 11, marginTop: 6, color: `${CY}cc` }}>{team.signed ?? 0} semnate · {team.unsigned ?? 0} lipsă</div>
      </div>

      {/* Right-top: personal TO-DO list (dictated to Jarvis) */}
      <div className="jv-panel" data-testid="hud-todo-panel" style={{ ...panel("right"), top: 110, width: 260 }}>
        <PanelTitle icon={<ListTodo size={13} />} text="TO DO" />
        {(d?.todo || []).length ? (
          <div style={{ maxHeight: 220, overflowY: "auto", paddingRight: 4 }}>
            {(d.todo || []).slice(0, 12).map((t, i) => (
              <div key={t.id || i} data-testid="hud-todo-item"
                style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", borderBottom: `1px solid ${CY}11` }}>
                <span style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0, borderRadius: 4, border: `1.5px solid ${OR}`, boxShadow: `0 0 8px ${OR}55` }} />
                <span style={{ fontSize: 12.5, color: "#dbeff7", lineHeight: 1.35 }}>{t.text}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: `${CY}88`, marginTop: 4, lineHeight: 1.5 }}>
            Nimic pe listă. Spune-i lui Jarvis „adaugă în to-do…" ca să apară aici.
          </div>
        )}
      </div>

      {/* Right-bottom: daily plan — shows the latest posted plan */}
      <div className="jv-panel" onClick={() => go("/doserne/plan")} data-testid="hud-go-plan" title="Deschide planul zilnic"
        style={{ ...panel("right"), bottom: 40, top: "auto", width: 300, ...clickable }}>
        <PanelTitle icon={<Calendar size={13} />} text="PLAN ZILNIC" />
        {d?.daily_plan?.latest ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", lineHeight: 1.15, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{d.daily_plan.latest.title || "(fără titlu)"}</div>
            <div style={{ fontSize: 10, color: `${CY}aa`, marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span>{d.daily_plan.latest.plan_date}</span>
              {d.daily_plan.latest.by && <span>· {d.daily_plan.latest.by}</span>}
              {d.daily_plan.latest.priority && d.daily_plan.latest.priority !== "normal" && (
                <span style={{ color: OR, fontWeight: 800, textTransform: "uppercase" }}>· {d.daily_plan.latest.priority}</span>
              )}
            </div>
            {d.daily_plan.latest.body && (
              <div onWheel={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
                className="jv-plan-body"
                style={{ marginTop: 8, maxHeight: 260, overflowY: "auto", paddingRight: 6, color: "#cfe6ef", fontSize: 12.5, lineHeight: 1.45 }}>
                <RichContent html={d.daily_plan.latest.body} />
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 11, color: `${CY}88`, marginTop: 4 }}>Niciun plan postat încă</div>
        )}
        <div style={{ marginTop: 10, fontSize: 10, color: `${CY}77`, display: "flex", alignItems: "center", gap: 5 }}>▸ Deschide planul</div>
      </div>

      {/* In-HUD photo lightbox: big photo + swipe/prev-next. Stays inside Jarvis. */}
      {hudLightbox && hudLightbox.items?.length > 0 && (() => {
        const cur = hudLightbox.items[hudLightbox.idx] || {};
        return (
          <div
            data-testid="hud-lightbox"
            onClick={() => setHudLightbox(null)}
            onWheel={(e) => e.stopPropagation()}
            style={{ position: "absolute", inset: 0, zIndex: 60, background: "rgba(1,4,10,0.94)",
              backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            {/* close */}
            <button type="button" data-testid="hud-lightbox-close"
              onClick={(e) => { e.stopPropagation(); setHudLightbox(null); }}
              style={{ position: "absolute", top: 20, right: 20, width: 44, height: 44, borderRadius: 999,
                border: `1px solid ${CY}55`, background: "rgba(3,10,20,0.7)", color: CY, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>
              ×
            </button>
            {/* prev */}
            {hudLightbox.items.length > 1 && (
              <button type="button" data-testid="hud-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); lbPrev(); }}
                style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", width: 48, height: 48,
                  borderRadius: 999, border: `1px solid ${CY}55`, background: "rgba(3,10,20,0.7)", color: CY, fontSize: 26, cursor: "pointer" }}>
                ‹
              </button>
            )}
            {/* next */}
            {hudLightbox.items.length > 1 && (
              <button type="button" data-testid="hud-lightbox-next"
                onClick={(e) => { e.stopPropagation(); lbNext(); }}
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", width: 48, height: 48,
                  borderRadius: 999, border: `1px solid ${CY}55`, background: "rgba(3,10,20,0.7)", color: CY, fontSize: 26, cursor: "pointer" }}>
                ›
              </button>
            )}
            {/* photo (swipeable) */}
            <div
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => { touchXRef.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchXRef.current == null) return;
                const dx = e.changedTouches[0].clientX - touchXRef.current;
                touchXRef.current = null;
                if (dx < -45) lbNext(); else if (dx > 45) lbPrev();
              }}
              style={{ maxWidth: "88vw", maxHeight: "82vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}
            >
              <img src={cur.url} alt={cur.name || "foto proiect"} data-testid="hud-lightbox-img"
                style={{ maxWidth: "88vw", maxHeight: "70vh", objectFit: "contain", borderRadius: 14,
                  border: `1px solid ${CY}33`, boxShadow: `0 0 60px ${CY}22` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, textShadow: `0 0 12px ${CY}66` }}>{cur.name || "Proiect"}</div>
                {cur.id && (
                  <button type="button" data-testid="hud-lightbox-open-project"
                    onClick={(e) => { e.stopPropagation(); go(`/doserne/prosjekter/${cur.id}`); }}
                    style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${CY}66`, background: `${CY}18`,
                      color: CY, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    Deschide proiectul ▸
                  </button>
                )}
              </div>
              {hudLightbox.items.length > 1 && (
                <div style={{ display: "flex", gap: 7 }}>
                  {hudLightbox.items.map((_, di) => (
                    <span key={di} style={{ width: di === hudLightbox.idx ? 22 : 7, height: 7, borderRadius: 999,
                      background: di === hudLightbox.idx ? CY : `${CY}44`, transition: "width .2s ease" }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* scanline */}
      <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${CY}55,transparent)`, animation: "jvscan 6s linear infinite", top: 0 }} />
    </div>
  );
}

const panel = (side) => ({
  position: "absolute", top: 130, [side]: 28, width: 240, zIndex: 4,
  background: "rgba(3,10,20,0.55)", border: "1px solid rgba(34,211,238,0.25)",
  borderRadius: 14, padding: "14px 16px", backdropFilter: "blur(4px)",
  boxShadow: "0 0 30px rgba(34,211,238,0.08)",
});

const PanelTitle = ({ icon, text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, letterSpacing: 2, color: "#22d3ee", marginBottom: 8, opacity: 0.85 }}>
    {icon}{text}
  </div>
);